import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { JOB_STATE_AR } from "@fanni/shared/job-state";
import { formatIQD } from "@fanni/shared/money";
import { INVOICE_STATUS_AR, STATUS_TONE, fmtDate, fmtDateTime } from "../lib/format.js";
import { ACCESS, useAuth } from "../lib/auth.js";
import {
  useAssets,
  useCreateAsset,
  useCreateCustomer,
  useCreateSite,
  useCustomer,
  useCustomers,
  useInvoices,
  useJobs,
} from "../lib/queries.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Note,
  PageHeader,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from "../ui/kit.js";

export function Customers() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useCustomers();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);

  const rows = useMemo(() => {
    const all = data?.customers ?? [];
    const term = q.trim();
    if (!term) return all;
    return all.filter((c) => c.name.includes(term) || c.phone.includes(term));
  }, [data, q]);

  return (
    <>
      <PageHeader
        title="الزبائن"
        subtitle={
          <>
            <span className="num">{data?.customers.length ?? 0}</span> زبون.
          </>
        }
        actions={
          <>
            <Input
              placeholder="بحث بالاسم أو الهاتف"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-56"
            />
            <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
              زبون جديد
            </Button>
          </>
        }
      />

      {error ? <ErrorNote error={error} className="mb-4" /> : null}

      <Card bodyClassName="">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>لا يوجد زبائن مطابقون.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>الاسم</Th>
                <Th>الهاتف</Th>
                <Th>هاتف بديل</Th>
                <Th>مُسجَّل منذ</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <Tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)}>
                  <Td className="font-medium">{c.name}</Td>
                  <Td>
                    <span className="num">{c.phone}</span>
                  </Td>
                  <Td>
                    <span className="num text-ink-soft">{c.altPhone ?? "—"}</span>
                  </Td>
                  <Td>
                    <span className="num text-ink-soft">{fmtDate(c.createdAt)}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {adding ? <NewCustomer onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function NewCustomer({ onClose }: { onClose: () => void }) {
  const create = useCreateCustomer();
  const [form, setForm] = useState({ name: "", phone: "", alt_phone: "", notes: "" });

  return (
    <Modal title="زبون جديد" onClose={onClose}>
      {create.error ? <ErrorNote error={create.error} className="mb-3" /> : null}
      <div className="space-y-3">
        <Field label="الاسم">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="الهاتف">
          <Input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label="هاتف بديل (اختياري)">
          <Input dir="ltr" value={form.alt_phone} onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} />
        </Field>
        <Field label="ملاحظات (اختياري)">
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </div>
      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={create.isPending || !form.name.trim() || form.phone.trim().length < 6}
        onClick={() =>
          create.mutate(
            {
              name: form.name.trim(),
              phone: form.phone.trim(),
              alt_phone: form.alt_phone.trim() || undefined,
              notes: form.notes.trim() || undefined,
            },
            { onSuccess: onClose },
          )
        }
      >
        حفظ
      </Button>
    </Modal>
  );
}

// ── detail ───────────────────────────────────────────────────────────────────

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const { data, isLoading, error } = useCustomer(id);
  const jobs = useJobs({ customer_id: id, limit: 200 });
  const invoices = useInvoices();
  const [addingSite, setAddingSite] = useState(false);

  const customerInvoices = useMemo(
    () => (invoices.data?.invoices ?? []).filter((i) => i.customerId === id),
    [invoices.data, id],
  );

  const outstanding = customerInvoices
    .filter((i) => i.status === "issued" || i.status === "partial")
    .reduce((a, i) => a + i.outstanding_fils, 0);

  if (isLoading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <>
      <PageHeader
        title={data.customer.name}
        subtitle={
          <span className="num">
            {data.customer.phone}
            {data.customer.altPhone ? ` · ${data.customer.altPhone}` : ""}
          </span>
        }
        actions={
          <Button size="sm" variant="primary" onClick={() => setAddingSite(true)}>
            موقع جديد
          </Button>
        }
      />

      {data.customer.notes ? <Note className="mb-4">{data.customer.notes}</Note> : null}

      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <Card title="المستحق عليه">
          {can(...ACCESS.money) ? (
            <>
              <p className="num text-2xl font-bold text-warn">{formatIQD(outstanding)}</p>
              <p className="mt-1 text-xs text-ink-faint">
                من <span className="num">{customerInvoices.length}</span> فاتورة.
              </p>
              {/*
                `customers.balance_fils` is a stored column that nothing in the API writes —
                the invoice and payment handlers update `invoices.paid_fils` and post to the
                ledger, never this. Showing it as a balance would be showing a zero that means
                nothing, so the real figure is summed from his invoices instead.
              */}
              <p className="mt-2 text-[11px] text-ink-faint">
                محسوب من فواتيره. الحقل <span className="num">balance_fils</span> في قاعدة البيانات
                لا يُحدّثه أي منفذ في الـ API بعد، فلا يُعرض.
              </p>
            </>
          ) : (
            <p className="text-xs text-ink-faint">لا تملك صلاحية عرض المال.</p>
          )}
        </Card>

        <Card title="المهام">
          <p className="num text-2xl font-bold">{jobs.data?.total ?? 0}</p>
          <p className="mt-1 text-xs text-ink-faint">إجمالاً منذ التسجيل.</p>
        </Card>

        <Card title="المواقع">
          <p className="num text-2xl font-bold">{data.sites.length}</p>
          <p className="mt-1 text-xs text-ink-faint">موقع مسجّل لهذا الزبون.</p>
        </Card>
      </div>

      <div className="space-y-4">
        {data.sites.map((site) => (
          <SitePanel key={site.id} siteId={site.id} label={site.label} address={site.address} />
        ))}
        {data.sites.length === 0 ? (
          <Card>
            <Empty>لا توجد مواقع. أضِف موقعاً قبل إنشاء مهمة.</Empty>
          </Card>
        ) : null}

        <Card title="سجل المهام" bodyClassName="">
          {jobs.isLoading ? (
            <Loading />
          ) : (jobs.data?.jobs.length ?? 0) === 0 ? (
            <Empty>لا توجد مهام لهذا الزبون.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>الرقم</Th>
                  <Th>الحالة</Th>
                  <Th>العنوان</Th>
                  <Th>الموقع</Th>
                  <Th>الفني</Th>
                  <Th>الموعد</Th>
                </tr>
              </thead>
              <tbody>
                {(jobs.data?.jobs ?? []).map((j) => (
                  <Tr key={j.id}>
                    <Td>
                      <Link to={`/jobs/${j.id}`} className="num text-accent hover:underline">
                        {j.number ? `#${j.number}` : "فتح"}
                      </Link>
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[j.status]}>{JOB_STATE_AR[j.status]}</Badge>
                    </Td>
                    <Td className="max-w-64 truncate">{j.title}</Td>
                    <Td className="text-ink-soft">{j.siteLabel ?? "—"}</Td>
                    <Td>{j.technicianName ?? "—"}</Td>
                    <Td>
                      <span className="num text-ink-soft">{fmtDateTime(j.scheduledFor)}</span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {can(...ACCESS.money) ? (
          <Card title="فواتيره" bodyClassName="">
            {customerInvoices.length === 0 ? (
              <Empty>لا توجد فواتير.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>الرقم</Th>
                    <Th>الحالة</Th>
                    <Th>المجموع</Th>
                    <Th>المتبقي</Th>
                    <Th>صدرت</Th>
                  </tr>
                </thead>
                <tbody>
                  {customerInvoices.map((i) => (
                    <Tr key={i.id}>
                      <Td>
                        <Link to={`/invoices/${i.id}`} className="num text-accent hover:underline">
                          {i.number ?? "—"}
                        </Link>
                      </Td>
                      <Td>
                        <Badge tone={i.status === "paid" ? "good" : i.status === "void" ? "neutral" : "warn"}>
                          {INVOICE_STATUS_AR[i.status]}
                        </Badge>
                      </Td>
                      <Td>
                        <span className="num">{formatIQD(i.totalFils)}</span>
                      </Td>
                      <Td>
                        <span className="num font-medium">{formatIQD(i.outstanding_fils)}</span>
                      </Td>
                      <Td>
                        <span className="num text-ink-soft">{fmtDate(i.issuedAt)}</span>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        ) : null}
      </div>

      {addingSite ? <NewSite customerId={data.customer.id} onClose={() => setAddingSite(false)} /> : null}
    </>
  );
}

/**
 * The unit history per site — the vertical's edge, and the reason an HVAC company chooses this
 * over a generic dispatch tool. "Which compressor is in the Karrada branch and when did we
 * last touch it" is a question no WhatsApp thread can answer.
 */
function SitePanel({
  siteId,
  label,
  address,
}: {
  siteId: string;
  label: string;
  address: string | null;
}) {
  const { data, isLoading } = useAssets(siteId);
  const [adding, setAdding] = useState(false);
  const assets = data?.assets ?? [];

  return (
    <Card
      title={
        <span>
          {label}
          {address ? <span className="mr-2 font-normal text-ink-faint">{address}</span> : null}
        </span>
      }
      actions={
        <Button size="sm" onClick={() => setAdding(true)}>
          إضافة وحدة
        </Button>
      }
      bodyClassName=""
    >
      {isLoading ? (
        <Loading />
      ) : assets.length === 0 ? (
        <Empty>لا توجد وحدات مسجّلة في هذا الموقع.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>النوع</Th>
              <Th>الماركة</Th>
              <Th>الموديل</Th>
              <Th>الرقم التسلسلي</Th>
              <Th>القدرة (BTU)</Th>
              <Th>تاريخ التركيب</Th>
              <Th>ملاحظات</Th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <Tr key={a.id}>
                <Td className="font-medium">{a.kind ?? "—"}</Td>
                <Td>{a.brand ?? "—"}</Td>
                <Td>{a.model ?? "—"}</Td>
                <Td>
                  <span className="num text-ink-soft">{a.serial ?? "—"}</span>
                </Td>
                <Td>
                  <span className="num">{a.capacityBtu ?? "—"}</span>
                </Td>
                <Td>
                  <span className="num text-ink-soft">{fmtDate(a.installedAt)}</span>
                </Td>
                <Td className="max-w-48 truncate text-ink-soft">{a.notes ?? "—"}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {adding ? <NewAsset siteId={siteId} onClose={() => setAdding(false)} /> : null}
    </Card>
  );
}

function NewSite({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const create = useCreateSite();
  const [form, setForm] = useState({ label: "", address: "" });

  return (
    <Modal title="موقع جديد" onClose={onClose}>
      {create.error ? <ErrorNote error={create.error} className="mb-3" /> : null}
      <div className="space-y-3">
        <Field label="اسم الموقع" hint="مثلاً: البيت، فرع الكرادة، الطابق الثالث">
          <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </Field>
        <Field label="العنوان (اختياري)">
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
      </div>
      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={create.isPending || !form.label.trim()}
        onClick={() =>
          create.mutate(
            { customer_id: customerId, label: form.label.trim(), address: form.address.trim() || undefined },
            { onSuccess: onClose },
          )
        }
      >
        حفظ
      </Button>
    </Modal>
  );
}

function NewAsset({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const create = useCreateAsset();
  const [form, setForm] = useState({
    kind: "",
    brand: "",
    model: "",
    serial: "",
    capacity_btu: "",
    notes: "",
  });

  return (
    <Modal title="وحدة جديدة" onClose={onClose}>
      {create.error ? <ErrorNote error={create.error} className="mb-3" /> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="النوع" hint="سبليت، شباك، مركزي…">
          <Input value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
        </Field>
        <Field label="الماركة">
          <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
        </Field>
        <Field label="الموديل">
          <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
        </Field>
        <Field label="الرقم التسلسلي">
          <Input dir="ltr" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} />
        </Field>
        <Field label="القدرة (BTU)">
          <Input
            type="number"
            dir="ltr"
            value={form.capacity_btu}
            onChange={(e) => setForm({ ...form, capacity_btu: e.target.value })}
          />
        </Field>
        <Field label="ملاحظات" className="col-span-2">
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </div>
      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={create.isPending}
        onClick={() => {
          const btu = Number(form.capacity_btu);
          create.mutate(
            {
              site_id: siteId,
              kind: form.kind.trim() || undefined,
              brand: form.brand.trim() || undefined,
              model: form.model.trim() || undefined,
              serial: form.serial.trim() || undefined,
              capacity_btu: Number.isFinite(btu) && btu > 0 ? Math.round(btu) : undefined,
              notes: form.notes.trim() || undefined,
            },
            { onSuccess: onClose },
          );
        }}
      >
        حفظ
      </Button>
    </Modal>
  );
}
